import {
  GiftCouponSummary,
  KakaoShareCallback,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { GiftOrderForAdmin } from '@shared/api/giftOrder/giftOrder.interface';

/**
 * 한 주문 내에서 서로 다른 두 개 이상의 쿠폰이 동일한 카카오톡 채팅방
 * (`hashChatId`) 으로 공유된 경우를 "cross-coupon overlap" 이라 부른다.
 *
 * - 같은 쿠폰이 같은 방에 여러 번 공유된 것은 cross-coupon 이 아니다
 *   (그 케이스는 per-coupon `shareKakaoCount` 가 잡는다).
 * - hashChatId 가 비어있는 콜백(=식별 불가)은 매칭에서 제외한다 (오인 방지).
 */
export interface CrossCouponHashOverlapGroup {
  /** `${chatType}:${hashChatId}` — 그룹 식별 키 */
  key: string;
  hashChatId: string;
  chatType: string;
  /** 이 채팅방으로 공유된 서로 다른 쿠폰 ID 목록 (등장 순) */
  couponIds: string[];
  /** 이 채팅방으로 들어온 모든 콜백 (디스플레이용 시간순 정렬) */
  callbacks: Array<
    KakaoShareCallback & {
      couponId: string;
      couponSeq?: number;
      couponCode?: string;
    }
  >;
}

export interface CrossCouponHashOverlapResult {
  /** 다른 쿠폰과 동일한 채팅방에 공유된 쿠폰 ID 집합 */
  overlapCouponIds: Set<string>;
  /** 2개 이상의 서로 다른 쿠폰이 같은 채팅방으로 공유된 그룹 목록 */
  groups: CrossCouponHashOverlapGroup[];
  /** hashChatId → 그룹 (UI 빠른 조회용) */
  groupByHash: Map<string, CrossCouponHashOverlapGroup>;
}

const EMPTY_RESULT: CrossCouponHashOverlapResult = {
  overlapCouponIds: new Set(),
  groups: [],
  groupByHash: new Map(),
};

export const computeCrossCouponHashOverlap = (
  coupons: GiftCouponSummary[] | undefined | null,
): CrossCouponHashOverlapResult => {
  if (!coupons || coupons.length === 0) return EMPTY_RESULT;

  const map = new Map<string, CrossCouponHashOverlapGroup>();

  for (const c of coupons) {
    const cbs = c.kakaoShareCallbacks ?? [];
    if (cbs.length === 0) continue;

    // 같은 쿠폰이 같은 방에 여러 번 보낸 케이스는 한 번만 couponIds 에 카운트
    const seenKeysForCoupon = new Set<string>();

    for (const cb of cbs) {
      const hash = cb.hashChatId;
      if (!hash) continue;
      const key = `${cb.chatType ?? ''}:${hash}`;

      const existing = map.get(key);
      const isFirstForCoupon = !seenKeysForCoupon.has(key);
      seenKeysForCoupon.add(key);

      if (!existing) {
        map.set(key, {
          key,
          hashChatId: hash,
          chatType: cb.chatType ?? '',
          couponIds: [c._id],
          callbacks: [{ ...cb, couponId: c._id, couponSeq: c.seq, couponCode: c.code }],
        });
        continue;
      }

      if (isFirstForCoupon) {
        existing.couponIds.push(c._id);
      }
      existing.callbacks.push({
        ...cb,
        couponId: c._id,
        couponSeq: c.seq,
        couponCode: c.code,
      });
    }
  }

  const groups = Array.from(map.values()).filter((g) => g.couponIds.length >= 2);

  for (const g of groups) {
    g.callbacks.sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
  }
  // 더 많은 쿠폰이 겹친 그룹부터 위로
  groups.sort((a, b) => {
    if (b.couponIds.length !== a.couponIds.length) {
      return b.couponIds.length - a.couponIds.length;
    }
    return b.callbacks.length - a.callbacks.length;
  });

  const overlapCouponIds = new Set<string>();
  const groupByHash = new Map<string, CrossCouponHashOverlapGroup>();
  for (const g of groups) {
    groupByHash.set(g.hashChatId, g);
    for (const id of g.couponIds) overlapCouponIds.add(id);
  }

  return { overlapCouponIds, groups, groupByHash };
};

/* ----------------------------------------------------------------------------
 * Cross-order overlap
 * --------------------------------------------------------------------------*/

/**
 * 서로 다른 두 개 이상의 주문(GiftOrder)의 쿠폰이 동일한 카카오톡 채팅방
 * (`hashChatId`) 으로 공유된 경우.
 *
 * - 같은 주문 안의 cross-coupon 중복은 `computeCrossCouponHashOverlap` 가 이미 처리.
 * - 본 함수는 검색 결과 같은 "여러 주문" 컨텍스트에서, 다른 주문의 쿠폰과 같은
 *   채팅방을 쓰는 경우를 추가로 잡는다.
 */
export interface CrossOrderHashOverlapEntry {
  orderId: string;
  couponId: string;
  couponSeq?: number;
  couponCode?: string;
}

export interface CrossOrderHashOverlapGroup {
  /** `${chatType}:${hashChatId}` */
  key: string;
  hashChatId: string;
  chatType: string;
  /** 등장 순으로 dedupe 된 (orderId, couponId) 항목 */
  entries: CrossOrderHashOverlapEntry[];
  /** 영향받는 서로 다른 주문 ID */
  orderIds: string[];
  /** 영향받는 서로 다른 쿠폰 ID */
  couponIds: string[];
  /** 이 채팅방으로 들어온 모든 콜백 (시간순) */
  callbacks: Array<
    KakaoShareCallback & {
      orderId: string;
      couponId: string;
      couponSeq?: number;
      couponCode?: string;
    }
  >;
}

export interface CrossOrderHashOverlapResult {
  /** 다른 주문의 쿠폰과 같은 카톡방을 공유한 주문 ID 집합 */
  overlapOrderIds: Set<string>;
  /** orderId → 그 주문이 참여한 cross-order 그룹 목록 */
  groupsByOrderId: Map<string, CrossOrderHashOverlapGroup[]>;
  /** 2개 이상의 서로 다른 주문이 같은 채팅방으로 공유한 그룹 목록 (위험 큰 순) */
  groups: CrossOrderHashOverlapGroup[];
}

const EMPTY_CROSS_ORDER_RESULT: CrossOrderHashOverlapResult = {
  overlapOrderIds: new Set(),
  groupsByOrderId: new Map(),
  groups: [],
};

interface InternalGroup {
  key: string;
  hashChatId: string;
  chatType: string;
  /** `${orderId}__${couponId}` → entry */
  entryMap: Map<string, CrossOrderHashOverlapEntry>;
  orderIdSet: Set<string>;
  couponIdSet: Set<string>;
  callbacks: CrossOrderHashOverlapGroup['callbacks'];
}

export const computeCrossOrderHashOverlap = (
  orders: GiftOrderForAdmin[] | undefined | null,
): CrossOrderHashOverlapResult => {
  if (!orders || orders.length === 0) return EMPTY_CROSS_ORDER_RESULT;

  const map = new Map<string, InternalGroup>();

  for (const order of orders) {
    const coupons = order.coupons;
    if (!coupons || coupons.length === 0) continue;
    for (const c of coupons) {
      const cbs = c.kakaoShareCallbacks ?? [];
      if (cbs.length === 0) continue;
      for (const cb of cbs) {
        const hash = cb.hashChatId;
        if (!hash) continue;
        const key = `${cb.chatType ?? ''}:${hash}`;
        let g = map.get(key);
        if (!g) {
          g = {
            key,
            hashChatId: hash,
            chatType: cb.chatType ?? '',
            entryMap: new Map(),
            orderIdSet: new Set(),
            couponIdSet: new Set(),
            callbacks: [],
          };
          map.set(key, g);
        }
        const entryKey = `${order._id}__${c._id}`;
        if (!g.entryMap.has(entryKey)) {
          g.entryMap.set(entryKey, {
            orderId: order._id,
            couponId: c._id,
            couponSeq: c.seq,
            couponCode: c.code,
          });
        }
        g.orderIdSet.add(order._id);
        g.couponIdSet.add(c._id);
        g.callbacks.push({
          ...cb,
          orderId: order._id,
          couponId: c._id,
          couponSeq: c.seq,
          couponCode: c.code,
        });
      }
    }
  }

  // 서로 다른 2개 이상의 "주문" 이 공유한 그룹만 cross-order overlap
  const allInternalGroups: InternalGroup[] = Array.from(map.values());
  const filtered: CrossOrderHashOverlapGroup[] = allInternalGroups
    .filter((g) => g.orderIdSet.size >= 2)
    .map((g) => {
      const callbacks = g.callbacks
        .slice()
        .sort(
          (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
      return {
        key: g.key,
        hashChatId: g.hashChatId,
        chatType: g.chatType,
        entries: Array.from(g.entryMap.values()),
        orderIds: Array.from(g.orderIdSet),
        couponIds: Array.from(g.couponIdSet),
        callbacks,
      };
    });

  filtered.sort((a, b) => {
    if (b.orderIds.length !== a.orderIds.length) {
      return b.orderIds.length - a.orderIds.length;
    }
    return b.couponIds.length - a.couponIds.length;
  });

  const overlapOrderIds = new Set<string>();
  const groupsByOrderId = new Map<string, CrossOrderHashOverlapGroup[]>();
  for (const g of filtered) {
    for (const oid of g.orderIds) {
      overlapOrderIds.add(oid);
      const list = groupsByOrderId.get(oid);
      if (list) list.push(g);
      else groupsByOrderId.set(oid, [g]);
    }
  }

  return { overlapOrderIds, groupsByOrderId, groups: filtered };
};
